import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faArrowRight,
  faCircleExclamation,
  faUserTag,
} from '@fortawesome/free-solid-svg-icons';
import {
  ROUND_STATE_BADGE,
  ROUND_STATE_LABELS,
  WAITING_FOR_LABELS,
} from '@/features/inspection/modification-state-rules';
import { ROUTES } from '@/routes/routes';
import type { StaffRequestThread } from '@/services/request-thread.service';

/**
 * The Request Thread header — identity, status, ownership, and what (if
 * anything) the reader must do.
 *
 * ── THE ONE RULE THIS COMPONENT EXISTS TO ENFORCE ───────────────────────────
 * Ownership is `currentOwner` and `waitingFor`. The assignee is a soft claim and
 * appears as a neutral, third-person attribution — "Assigned to {name}" — which
 * reads identically no matter who is looking at it.
 *
 * The phrase "You are handling this request" does not exist here and must not be
 * reintroduced. On INS-00201 the round is Operations-owned while carrying a
 * STALE Commercial claim, and that phrase told the Commercial user they owned a
 * request they could not act on. The only first-person statement on this screen
 * is "Waiting on you", and it comes from `capabilities.actionRequired` — the
 * server's answer to "does this caller have an action here?" — never from the
 * assignee and never from a department the client inferred.
 */

function OwnershipChip({ children }: { children: React.ReactNode }) {
  return <span className="text-xs text-muted-foreground">{children}</span>;
}

export function RequestThreadHeader({ thread }: { thread: StaffRequestThread }) {
  const { chain, capabilities } = thread;
  const isClosed = thread.closedAt !== null;

  // "you" is legitimate here ONLY because the server said this caller has an
  // action. It is not a department comparison and not an assignee check.
  const waitingLabel = capabilities.actionRequired
    ? 'you'
    : thread.waitingFor
      ? WAITING_FOR_LABELS[thread.waitingFor]
      : null;

  return (
    <header className="space-y-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <h1 className="text-lg font-semibold text-foreground">Request #{thread.roundNumber}</h1>
        <span
          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${ROUND_STATE_BADGE[thread.state]}`}
        >
          {ROUND_STATE_LABELS[thread.state]}
        </span>
        {chain.totalRequests > 1 && (
          <OwnershipChip>
            Request {chain.requestNumber} of {chain.totalRequests} on this job
          </OwnershipChip>
        )}
      </div>

      {capabilities.actionRequired && (
        <p
          role="status"
          data-testid="thread-action-required"
          className="flex items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm font-medium text-amber-800 dark:text-amber-200"
        >
          <FontAwesomeIcon icon={faCircleExclamation} className="h-4 w-4" />
          Action required from you
        </p>
      )}

      <div className="flex flex-wrap items-center gap-x-2 gap-y-1" data-testid="thread-ownership">
        {isClosed ? (
          // A closed round is owned by nobody and waits for nobody. Saying
          // otherwise would be inventing an owner for a finished conversation.
          <OwnershipChip>This request is closed.</OwnershipChip>
        ) : (
          <>
            {thread.currentOwner && (
              <OwnershipChip>
                Owner:{' '}
                <span className="font-medium text-foreground">
                  {thread.currentOwner === 'COMMERCIAL' ? 'Commercial' : 'Operations'}
                </span>
              </OwnershipChip>
            )}
            {waitingLabel && (
              <OwnershipChip>
                ·{' '}
                <span
                  className={
                    capabilities.actionRequired ? 'font-medium text-destructive' : undefined
                  }
                >
                  Waiting on {waitingLabel}
                </span>
              </OwnershipChip>
            )}
          </>
        )}

        {thread.assignee && (
          // Attribution, not authority. Third person, always — and labelled so a
          // reader cannot mistake a claim for ownership.
          <span
            data-testid="thread-assignee"
            title="A soft claim recorded when this person first acted. It does not decide ownership or what anyone may do."
            className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-0.5 text-xs text-muted-foreground"
          >
            <FontAwesomeIcon icon={faUserTag} className="h-3 w-3" />
            Assigned to {thread.assignee.name}
          </span>
        )}
      </div>

      {(chain.previous || chain.next) && (
        <nav aria-label="Request chain" className="flex flex-wrap items-center gap-3 text-xs">
          {chain.previous && (
            <Link
              to={ROUTES.REQUEST_THREAD.replace(':roundId', chain.previous.roundId)}
              className="inline-flex items-center gap-1.5 text-primary hover:underline"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="h-3 w-3" />
              Request #{chain.previous.roundNumber}
            </Link>
          )}
          {chain.next && (
            <Link
              to={ROUTES.REQUEST_THREAD.replace(':roundId', chain.next.roundId)}
              className="inline-flex items-center gap-1.5 text-primary hover:underline"
            >
              Request #{chain.next.roundNumber}
              <FontAwesomeIcon icon={faArrowRight} className="h-3 w-3" />
            </Link>
          )}
        </nav>
      )}
    </header>
  );
}
